// Team-colour mask generation — chroma-based, DOM-free.
//
// A tint mask is an 8-bit coverage map over one image: per texel, how much of it
// belongs to the owning team's colour. The client samples it in Custom/SpriteTint
// and recolours the covered region to the team colour at each texel's own
// luminance, so the art's shading and detail survive the recolour.
//
// The central idea for GENERATING one: split each RGB colour into a luminance
// component (its projection onto the grey axis (1,1,1)) and a chroma component
// (everything left over). The chroma vector's DIRECTION is the hue; its
// MAGNITUDE is colourfulness. A texel is covered when its chroma direction
// aligns with a target colour's chroma direction — which is why (1,0,0) and
// (0.5,0,0) match perfectly (same direction, different length), while any grey
// matches nothing at all (it has no chroma direction).
//
// Everything here operates on plain RGBA Uint8ClampedArrays (the layout of
// ImageData.data), so it is equally usable from a worker or a Node script.

export interface RGB {
    /** red,   0..1 */ r: number;
    /** green, 0..1 */ g: number;
    /** blue,  0..1 */ b: number;
}

export interface MaskOptions {
    // One or more source colours to match, channels 0..1. Only each colour's
    // chroma DIRECTION is used. A texel's coverage is the MAXIMUM of its match
    // against each target — it is covered if it resembles ANY of the target hues,
    // which lets one team-coloured material span several distinct hues. Max
    // rather than sum/average: a texel matching one target stays fully covered
    // without dilution, and one partially matching two is not inflated to a full
    // match. Neutral (near-grey) targets are ignored, having nothing to match.
    targets: RGB[];
    // Falloff exponent on the chroma-direction match (>= 0). 1 is linear; higher
    // tightens it so only near-exact hues score high. Default 1.
    sharpness?: number;
    // How much a texel's own colourfulness scales its coverage, 0..1.
    //   0 → pure direction match: a faint tint of the right hue counts as much as
    //       a vivid one.
    //   1 → coverage is additionally scaled by how saturated the texel actually
    //       is, so faint tints score low.
    // Default 0.
    saturationInfluence?: number;
    // Chroma magnitudes at or below this count as neutral (coverage 0), which
    // suppresses hue noise in near-grey texels. 0..~0.8. Default 0.02.
    neutralThreshold?: number;
}

// Unit vector along the neutral / grey axis (1,1,1)/sqrt(3).
const GREY = 1 / Math.sqrt(3);

// Rec. 709 luminance, applied to LINEAR channel values. This must stay identical
// to TEAM_LUMA in Client/Assets/Shaders/SpriteTint.shader (its non-gamma branch),
// because it is the luminance the team colour is applied at — any disagreement
// makes the editor's preview a lie about what the game will draw.
//
// Linear, not sRGB, because the game renders in Linear colour space: the sprite
// texture is sRGB, so the GPU has already converted it by the time the shader
// sees it. Everything on the RENDERING side of this file therefore works in
// linear and converts back at the end. Note the deliberate split — the chroma
// MATCHING above stays in sRGB, because which hues belong to the team is an
// authoring judgement made in the space the artist sees, and it never reaches
// the shader.
const LUMA_R = 0.2126;
const LUMA_G = 0.7152;
const LUMA_B = 0.0722;

// sRGB transfer function, the exact piecewise form — the same curve the GPU's
// sRGB sampler and Unity's Color.linear apply, so the preview and the shader
// agree texel for texel rather than approximately.
const SRGB_TO_LINEAR = (() => {
    const table = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
        const v = i / 255;
        table[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    }
    return table;
})();

function srgbToLinear(channel01: number): number {
    return channel01 <= 0.04045 ? channel01 / 12.92 : Math.pow((channel01 + 0.055) / 1.055, 2.4);
}

function linearToSrgb(linear: number): number {
    if (linear <= 0) return 0;
    if (linear >= 1) return 1;
    return linear <= 0.0031308 ? linear * 12.92 : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
}

// Coverage at or above which a texel counts as team-coloured for the purpose of
// measuring the region (gain suggestion), rather than rendering it.
const MEASURE_THRESHOLD = 0.5;

// How much faster the desaturation reaches full strength than the tint does —
// see renderPreview. MUST match TEAM_DESAT_BOOST in
// Client/Assets/Shaders/SpriteTint.shader.
const DESAT_BOOST = 3;

/**
 * Chroma vector of an RGB colour: the component orthogonal to the grey axis.
 * Written into `out` (length 3) to avoid allocating per texel.
 */
function chromaVector(r: number, g: number, b: number, out: Float64Array): void {
    // Projection length onto the unit grey axis.
    const p = (r + g + b) * GREY;
    out[0] = r - p * GREY;
    out[1] = g - p * GREY;
    out[2] = b - p * GREY;
}

function scoreFromChroma(
    c: Float64Array,
    cMag: number,
    t: Float64Array,
    tMag: number,
    sharpness: number,
    saturationInfluence: number,
    neutralThreshold: number,
): number {
    if (cMag <= neutralThreshold) return 0;

    // Cosine of the angle between the two chroma directions, clamped to 0..1.
    let cos = (c[0] * t[0] + c[1] * t[1] + c[2] * t[2]) / (cMag * tMag);
    if (cos <= 0) return 0;
    if (cos > 1) cos = 1;

    let score = sharpness === 1 ? cos : Math.pow(cos, sharpness);

    if (saturationInfluence > 0) {
        // Relative colourfulness: 1 when the texel is as saturated as the target.
        const rel = Math.min(1, cMag / tMag);
        score *= 1 - saturationInfluence + saturationInfluence * rel;
    }
    return score;
}

/**
 * Coverage score of a single colour against a single target, 0..1. Exposed for
 * callers that want to score one colour (a UI swatch, a test) without running a
 * whole image through the engine.
 */
export function chromaMatch(pixel: RGB, target: RGB, options?: Omit<MaskOptions, 'targets'>): number {
    const sharpness = options?.sharpness ?? 1;
    const saturationInfluence = options?.saturationInfluence ?? 0;
    const neutralThreshold = options?.neutralThreshold ?? 0.02;

    const t = new Float64Array(3);
    chromaVector(target.r, target.g, target.b, t);
    const tMag = Math.hypot(t[0], t[1], t[2]);
    if (tMag < 1e-6) return 0;      // the target itself is neutral — nothing to match

    const c = new Float64Array(3);
    chromaVector(pixel.r, pixel.g, pixel.b, c);
    const cMag = Math.hypot(c[0], c[1], c[2]);
    return scoreFromChroma(c, cMag, t, tMag, sharpness, saturationInfluence, neutralThreshold);
}

interface PreparedTarget {
    v: Float64Array;
    mag: number;
}

/** Precompute each target's chroma vector + magnitude, dropping neutrals. */
function prepareTargets(targets: RGB[]): PreparedTarget[] {
    const out: PreparedTarget[] = [];
    for (const target of targets) {
        const v = new Float64Array(3);
        chromaVector(target.r, target.g, target.b, v);
        const mag = Math.hypot(v[0], v[1], v[2]);
        if (mag >= 1e-6) out.push({v, mag});
    }
    return out;
}

/**
 * Reusable engine for live mask authoring. Construct it once per image — it
 * precomputes every texel's chroma vector and luma, which is the expensive part —
 * then call computeMask repeatedly as the targets and sliders move. That path is
 * a dot product per texel per target, which keeps a 1K sprite interactive.
 */
export class TintMaskEngine {
    readonly width: number;
    readonly height: number;

    private readonly src: Uint8ClampedArray;
    // Per-texel precomputed data, one entry each.
    private readonly cx: Float32Array;
    private readonly cy: Float32Array;
    private readonly cz: Float32Array;
    private readonly cMag: Float32Array;
    private readonly luma: Float32Array;

    /** @param rgba source pixels, RGBA, length >= width*height*4 (ImageData.data). */
    constructor(width: number, height: number, rgba: Uint8ClampedArray) {
        if (rgba.length < width * height * 4) {
            throw new Error('rgba buffer is smaller than width*height*4');
        }
        this.width = width;
        this.height = height;
        this.src = rgba;

        const n = width * height;
        this.cx = new Float32Array(n);
        this.cy = new Float32Array(n);
        this.cz = new Float32Array(n);
        this.cMag = new Float32Array(n);
        this.luma = new Float32Array(n);

        const c = new Float64Array(3);
        for (let i = 0, p = 0; i < n; i++, p += 4) {
            const r = rgba[p] / 255;
            const g = rgba[p + 1] / 255;
            const b = rgba[p + 2] / 255;
            // Chroma from the sRGB values (the authoring space); luminance from
            // the linear ones (the rendering space) — see the constants above.
            chromaVector(r, g, b, c);
            this.cx[i] = c[0];
            this.cy[i] = c[1];
            this.cz[i] = c[2];
            this.cMag[i] = Math.hypot(c[0], c[1], c[2]);
            this.luma[i] = LUMA_R * SRGB_TO_LINEAR[rgba[p]]
                + LUMA_G * SRGB_TO_LINEAR[rgba[p + 1]]
                + LUMA_B * SRGB_TO_LINEAR[rgba[p + 2]];
        }
    }

    /**
     * The mask itself: one byte of coverage per texel, row-major, ready to be
     * written straight out as an 8bpp greyscale PNG.
     *
     * Fully-transparent texels are forced to 0. They are invisible in game, but a
     * mask is authored against colour the eye can't see there (PNG edges commonly
     * carry arbitrary RGB under alpha 0), and leaving them set makes the exported
     * mask look wrong for no benefit.
     */
    computeMask(options: MaskOptions): Uint8ClampedArray {
        const sharpness = options.sharpness ?? 1;
        const saturationInfluence = options.saturationInfluence ?? 0;
        const neutralThreshold = options.neutralThreshold ?? 0.02;
        const targets = prepareTargets(options.targets);

        const n = this.width * this.height;
        const mask = new Uint8ClampedArray(n);
        if (targets.length === 0) return mask;

        const c = new Float64Array(3);
        for (let i = 0, p = 0; i < n; i++, p += 4) {
            if (this.src[p + 3] === 0) continue;

            const cMag = this.cMag[i];
            if (cMag <= neutralThreshold) continue;

            c[0] = this.cx[i];
            c[1] = this.cy[i];
            c[2] = this.cz[i];

            // Combined coverage = the best (max) match across all targets.
            let score = 0;
            for (const target of targets) {
                const s = scoreFromChroma(c, cMag, target.v, target.mag,
                    sharpness, saturationInfluence, neutralThreshold);
                if (s > score) score = s;
            }
            mask[i] = score * 255;
        }
        return mask;
    }

    /**
     * What the CLIENT will draw: the source art with the masked region recoloured
     * to `teamColor`.
     *
     * This is deliberately a transcription of the team-colour block in
     * Custom/SpriteTint rather than an idealised preview — same luminance
     * coefficients, same saturate, same lerp, same order, and crucially the same
     * COLOUR SPACE — because its whole job is to be trusted. Change one, change
     * the other.
     *
     * The lerp in particular has to happen in linear: it is what decides the
     * colour of every partially-covered texel, i.e. the entire anti-aliased edge
     * of every recoloured region, and doing it in sRGB instead visibly changes
     * the fringe where the art's own hue meets the team colour.
     *
     * The two-stage blend (desaturate on a boosted coverage, then tint on the
     * true one) is there so a partially covered texel leaves behind neutral grey
     * rather than a fraction of the art's authored hue. Fully covered texels are
     * identical either way. The shader carries the full reasoning.
     *
     * Cheap enough to call once per team colour on every slider move, since the
     * mask it consumes is already computed.
     */
    renderPreview(mask: Uint8ClampedArray, teamColor: RGB, gain = 1): Uint8ClampedArray {
        const n = this.width * this.height;
        const out = new Uint8ClampedArray(n * 4);
        out.set(this.src.subarray(0, n * 4));   // start from the art, alpha included

        // The team colour is authored in sRGB (that is what TeamsManager holds and
        // what a colour picker gives back); TeamTint.SetColor converts it before
        // it reaches the shader, so convert it here too.
        const teamR = srgbToLinear(teamColor.r);
        const teamG = srgbToLinear(teamColor.g);
        const teamB = srgbToLinear(teamColor.b);

        for (let i = 0, p = 0; i < n; i++, p += 4) {
            const coverage = mask[i] / 255;
            if (coverage === 0) continue;

            // Desaturate toward the UNGAINED luminance (gaining it here would
            // brighten partly-covered texels into a pale halo), tint with the
            // gained one.
            const luma = this.luma[i];
            const teamLuma = Math.min(1, luma * gain);
            const desaturation = Math.min(1, coverage * DESAT_BOOST);

            const r = SRGB_TO_LINEAR[this.src[p]];
            const g = SRGB_TO_LINEAR[this.src[p + 1]];
            const b = SRGB_TO_LINEAR[this.src[p + 2]];
            const baseR = r + (luma - r) * desaturation;
            const baseG = g + (luma - g) * desaturation;
            const baseB = b + (luma - b) * desaturation;

            out[p] = 255 * linearToSrgb(baseR + (teamLuma * teamR - baseR) * coverage);
            out[p + 1] = 255 * linearToSrgb(baseG + (teamLuma * teamG - baseG) * coverage);
            out[p + 2] = 255 * linearToSrgb(baseB + (teamLuma * teamB - baseB) * coverage);
            // Alpha is the art's own and is never touched by the tint.
        }
        return out;
    }

    /**
     * Suggest a starting target by finding the most colourful texel in the source
     * (largest chroma magnitude), so the dialog opens on something useful instead
     * of an arbitrary red. Null when the image is entirely neutral.
     */
    suggestTarget(): RGB | null {
        const n = this.width * this.height;
        let best = -1;
        let bestMag = 0;
        for (let i = 0; i < n; i++) {
            if (this.src[i * 4 + 3] === 0) continue;
            if (this.cMag[i] > bestMag) {
                bestMag = this.cMag[i];
                best = i;
            }
        }
        if (best < 0 || bestMag < 1e-4) return null;
        const p = best * 4;
        return {r: this.src[p] / 255, g: this.src[p + 1] / 255, b: this.src[p + 2] / 255};
    }

    /**
     * Suggest a gain that normalises the covered region's brightness: scaled so
     * the brightest strongly-covered texel reaches `reference` LINEAR luminance
     * (default 1 = white). Without it, a region that is dark in the source art —
     * near-black armour — takes every team colour as a muddy near-black, and the
     * teams become indistinguishable, which is the entire point of the feature.
     *
     * Uses the peak rather than the mean, so it never clips detail below the
     * brightest covered texel. Returns 1 when nothing is covered.
     */
    suggestGain(mask: Uint8ClampedArray, reference = 1): number {
        const n = this.width * this.height;
        let peak = 0;
        for (let i = 0; i < n; i++) {
            if (mask[i] / 255 >= MEASURE_THRESHOLD && this.luma[i] > peak) peak = this.luma[i];
        }
        if (peak < 1e-4) return 1;
        return reference / peak;
    }

    /** Fraction of the art's visible texels the mask covers, 0..1. */
    coverage(mask: Uint8ClampedArray): number {
        const n = this.width * this.height;
        let visible = 0;
        let covered = 0;
        for (let i = 0; i < n; i++) {
            if (this.src[i * 4 + 3] === 0) continue;
            visible++;
            if (mask[i] / 255 >= MEASURE_THRESHOLD) covered++;
        }
        return visible === 0 ? 0 : covered / visible;
    }
}

/** Convert an 0..1 RGB to a `#rrggbb` hex string. */
export function rgbToHex({r, g, b}: RGB): string {
    const to = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
}

/** Parse a `#rrggbb` (or `#rgb`) hex string to an 0..1 RGB. */
export function hexToRgb(hex: string): RGB {
    let h = hex.replace(/^#/, '').trim();
    if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
    const int = parseInt(h, 16);
    return {r: ((int >> 16) & 255) / 255, g: ((int >> 8) & 255) / 255, b: (int & 255) / 255};
}
